/**
 * Zugang zu den Oberflächentexten und zur ortsabhängigen Formatierung.
 *
 * Kein Übersetzungsrahmenwerk: Bei einer Sprache wäre das Aufwand ohne
 * Gegenwert. Die Struktur ist aber die, die ein Rahmenwerk später übernehmen
 * kann — Texte liegen vollständig außerhalb der Komponenten (E-F-12).
 */
import { de, type Translations } from "./de";

export const LOCALE = "de-DE";

/** Aktive Sprache. Für eine zweite Sprache hier auswählen, sonst nichts ändern. */
export const t: Translations = de;

const dateTime = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const dateOnly = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const timeOnly = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit" });

const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

export function formatDateTime(value: Date | number | string): string {
  return dateTime.format(new Date(value));
}

export function formatDate(value: Date | number | string): string {
  return dateOnly.format(new Date(value));
}

export function formatTime(value: Date | number | string): string {
  return timeOnly.format(new Date(value));
}

/** „vor 3 Minuten", „in 2 Stunden" — für Warteschlange und Verlauf. */
export function formatRelative(value: Date | number | string): string {
  const deltaMs = new Date(value).getTime() - Date.now();
  const minutes = Math.round(deltaMs / 60_000);
  if (Math.abs(minutes) < 1) return relative.format(Math.round(deltaMs / 1000), "second");
  if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relative.format(hours, "hour");
  return relative.format(Math.round(hours / 24), "day");
}

export function formatNumber(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Dezimalwerte kommen als Zeichenkette vom Backend (Pydantic `Decimal`).
 * Umwandeln erst hier, allein für die Anzeige — gerechnet wird damit nicht.
 */
export function formatDecimal(value: string | null | undefined, fractionDigits = 2): string {
  if (value === null || value === undefined || value === "") return t.common.none;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return t.common.none;
  return formatNumber(parsed, fractionDigits);
}

/** Fläche mit Einheit, etwa „24,50 m²". */
export function formatArea(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return t.common.none;
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return t.common.none;
  return `${formatNumber(parsed, 2)} m²`;
}

/** Sekundenangabe als mm:ss — für die laufende Sprachaufnahme. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
